/**
 * 백오피스 enum → 한국어 라벨 단일 소스.
 *
 * rowEnum 노출 결함(G-all-3) 해소용. `t(`orders.paymentMethod.${value}`)` 처럼
 * 템플릿 키로 i18n 을 조회하면 value 가 null/undefined/미지원 값일 때 번역이 없어
 * `orders.paymentMethod.undefined` 같은 RAW KEY 가 화면에 노출된다.
 *
 * `labelOf(map, value)` 는 그런 경우 절대 raw key 를 반환하지 않고 fallback('-')
 * 을 돌려준다. 라벨 맵은 public/locales/ko-KR/*.json 과 1:1 로 맞춘다.
 */

export type LabelMap = Readonly<Record<string, string>>;

const UNSET_FALLBACK = '-';

/**
 * enum 값을 한국어 라벨로 안전 변환한다.
 * null/undefined/빈문자/맵에 없는 값이면 fallback 을 반환한다 (raw key 노출 방지).
 */
export const labelOf = (
  map: LabelMap,
  value: string | null | undefined,
  fallback: string = UNSET_FALLBACK,
): string => {
  if (value === null || value === undefined || value === '') return fallback;
  return map[value] ?? fallback;
};

// ---------------------------------------------------------------------------
// 주문 (orders)
// ---------------------------------------------------------------------------

// 처리 이력(타임라인)의 canonical orderStatus enum → 한글. 고도몰 노출이름(무공백)으로 정합.
// HOLD_FOR_RELEASE/READY_FOR_FULFILLMENT(독립몰 전용 단계)는 고도몰 24엔 없어 '상품준비중'으로
// 흡수(Lead 확정 Q1). 취소 세분(자동/품절/관리자/고객요청)은 BE displayLabelCode 소관이므로
// 타임라인은 canonical 전이명만 무공백 표기(FE 재도출 금지 — Lead Q2).
export const ORDER_STATUS_LABELS: LabelMap = {
  CREATED: '주문생성',
  PENDING_PAYMENT: '입금대기',
  PAYMENT_CONFIRMED: '결제완료',
  HOLD_FOR_RELEASE: '상품준비중',
  READY_FOR_FULFILLMENT: '상품준비중',
  FULFILLING: '배송중',
  FULFILLED: '배송완료',
  COMPLETED: '구매확정',
  CANCEL_REQUESTED: '취소접수',
  CANCELED: '취소완료',
  PARTIALLY_CANCELED: '부분취소',
  EXPIRED: '결제만료',
  FAILED: '결제실패',
};

export const FULFILLMENT_TYPE_LABELS: LabelMap = {
  DELIVERY: '택배 배송',
  PICKUP: '현장 수령',
};

// ⚠ 현재 미렌더(선언만). 필터 드롭다운은 i18n `orders.fulfillmentStatus.*` 사용.
// 13차 '출고' 비표준 잔재 제거 위해 고도몰 표준(무공백)으로 정합(제거는 보류 — Lead 지시).
export const FULFILLMENT_STATUS_LABELS: LabelMap = {
  PENDING: '배송준비중',
  READY: '배송대기',
  FULFILLING: '배송중',
  FULFILLED: '배송완료',
  FAILED: '배송실패',
};

export const PICKUP_STATUS_LABELS: LabelMap = {
  WAITING_PREP: '픽업 준비 대기',
  READY: '픽업 가능',
  IN_PROGRESS: '픽업 진행',
  COMPLETED: '픽업 완료',
  NO_SHOW: '픽업 미수령',
  CANCELED: '픽업 취소',
};

// 택배사 이름은 배송 업체 관리(GET /admin/v1/shipping/couriers)가 정본이다(QA-510) — 고정 목록을 두지 않는다.

// ---------------------------------------------------------------------------
// 결제 (payments) — orders 와 공유
// ---------------------------------------------------------------------------

export const PAYMENT_METHOD_LABELS: LabelMap = {
  CARD: '신용/체크카드',
  VIRTUAL_ACCOUNT: '가상계좌',
  KAKAOPAY: '카카오페이',
  TOSSPAY: '토스페이',
  // M-4 무통장(직접입금) — BO 계좌 마스터 안내 + 수동 입금확인 대상.
  // 고도몰 표준 용어 "무통장입금"(붙여쓰기), manualOrder 라벨과 정합.
  BANK_TRANSFER: '무통장입금',
  // QA-438 — BE PaymentMethod 의 나머지 값. 종전엔 라벨이 없어 결제 상세 "결제 수단"이 '-' 로 보였다(계좌이체 등).
  TRANSFER: '계좌이체',
  MOBILE_PHONE: '휴대폰',
  EASY_PAY: '간편결제',
  OVERSEAS_CARD: '해외카드',
  PAYPAL: 'PayPal',
  ALIPAY_PLUS: 'Alipay+',
  PAYPAY: 'PayPay',
  MANUAL_PAID: '수기 결제',
};

export const PAYMENT_STATUS_LABELS: LabelMap = {
  INITIATED: '결제 시작',
  AUTH_PENDING: '승인 대기',
  AUTHORIZED: '승인 완료',
  CAPTURED: '결제 확정',
  FAILED: '결제 실패',
  CANCELED: '결제 취소',
  PARTIALLY_REFUNDED: '부분 환불',
  REFUNDED: '환불 완료',
  REFUND_FAILED: '환불 실패',
  EXPIRED: '만료',
};

export const PAYMENT_PROVIDER_LABELS: LabelMap = {
  TOSS: 'TossPayments',
  KAKAO: 'KakaoPay',
};

export const REFUND_REASON_LABELS: LabelMap = {
  CHANGE_OF_MIND: '고객 요청',
  WRONG_OPTION: '옵션 오류',
  DELIVERY_ISSUE: '배송 지연',
  PRODUCT_DEFECT: '상품 하자',
  DUPLICATE_PAYMENT: '중복 결제',
  OPERATOR_ADJUSTMENT: '운영자 보정',
  OTHER: '기타',
};

export const REFUND_STATUS_LABELS: LabelMap = {
  REQUESTED: '요청됨',
  IN_PROGRESS: '처리 중',
  COMPLETED: '완료',
  FAILED: '실패',
};

export const WEBHOOK_EVENT_TYPE_LABELS: LabelMap = {
  PAYMENT_STATUS_CHANGED: '상태 변경',
  PAYMENT_AUTHORIZED: '승인',
  PAYMENT_CAPTURED: '매입',
  PAYMENT_CANCELED: '취소',
  PAYMENT_FAILED: '실패',
  PAYMENT_EXPIRED: '만료',
  DEPOSIT_CALLBACK: '입금 콜백',
  REFUND_COMPLETED: '환불 완료',
  REFUND_FAILED: '환불 실패',
};

// ---------------------------------------------------------------------------
// 클레임 (claims)
// ---------------------------------------------------------------------------

export const CLAIM_TYPE_LABELS: LabelMap = {
  CANCEL: '취소',
  RETURN: '반품',
  EXCHANGE: '교환',
};

export const CLAIM_STATUS_LABELS: LabelMap = {
  REQUESTED: '신청',
  UNDER_REVIEW: '검토 중',
  APPROVED: '승인',
  REJECTED: '반려',
  COLLECTING: '회수 중',
  REFUND_PENDING: '환불 대기',
  COMPLETED: '완료',
  FAILED: '실패',
};

export const CLAIM_REASON_CODE_LABELS: LabelMap = {
  DEFECTIVE: '상품 불량/파손',
  WRONG_DELIVERY: '오배송/누락',
  MIND_CHANGE: '단순 변심',
  SHIPPING_DELAY: '배송 지연',
  OTHER: '기타',
};

export const CLAIM_ACTOR_TYPE_LABELS: LabelMap = {
  USER: '고객',
  ADMIN: '운영자',
  SYSTEM: '시스템',
};

// ---------------------------------------------------------------------------
// 펀딩 (funding)
// ---------------------------------------------------------------------------

export const FUNDING_STATUS_LABELS: LabelMap = {
  DRAFT: '초안',
  ACTIVE: '진행 중',
  SUCCEEDED: '성공',
  FAILED: '실패',
  CANCELLED: '취소',
  SETTLED: '정산 완료',
};

export const FUNDING_PLEDGE_STATUS_LABELS: LabelMap = {
  PENDING: '결제 대기',
  PAID: '결제 완료',
  CANCELLED: '취소',
  REFUNDED: '환불',
  SETTLED: '정산 완료',
};

export const FUNDING_REWARD_TYPE_LABELS: LabelMap = {
  DIGITAL: '디지털',
  PHYSICAL: '실물',
  EXPERIENCE: '체험',
};

// ---------------------------------------------------------------------------
// 재고 (inventory)
// ---------------------------------------------------------------------------

export const INVENTORY_STATUS_LABELS: LabelMap = {
  NORMAL: '정상',
  LOW: '부족',
  OUT_OF_STOCK: '품절',
};

// ---------------------------------------------------------------------------
// 통합 디스플레이 상태 (status namespace — orders/payments 공유)
// ---------------------------------------------------------------------------

// ⚠ 현재 미렌더(선언만). LIVE pill 경로는 i18n `status.*`(orders/payments) + display-status.ts MAP.
// 13차 spaced/'출고' 잔재 제거 위해 orders.json `status.*` 와 동일 라벨(무공백)로 정합
// (READY_TO_SHIP→상품준비중: Lead Q1). 제거는 보류(Lead 지시). PHASE 2-B 에서 displayLabelCode 맵으로 대체 예정.
export const DISPLAY_STATUS_LABELS: LabelMap = {
  PAYMENT_PROCESSING: '결제진행중',
  PAID: '결제완료',
  PAYMENT_EXPIRED: '결제만료',
  PAYMENT_FAILED: '결제실패',
  PROCESSING: '상품준비중',
  READY_TO_SHIP: '상품준비중',
  SHIPPED: '배송중',
  DELIVERED: '배송완료',
  FULFILLMENT_FAILED: '배송실패',
  PICKUP_WAITING_PREP: '픽업준비',
  PICKUP_READY: '픽업가능',
  PICKUP_IN_PROGRESS: '픽업진행',
  PICKUP_COMPLETED: '픽업완료',
  PICKUP_NO_SHOW: '픽업미수령',
  PICKUP_CANCELED: '픽업취소',
  CLAIM_ACTIVE: '클레임진행',
  CLAIM_REQUESTED: '클레임요청',
  CLAIM_UNDER_REVIEW: '검토중',
  CLAIM_APPROVED: '승인',
  CLAIM_REJECTED: '반려',
  CLAIM_COLLECTING: '회수중',
  CLAIM_REFUND_PENDING: '환불접수',
  CLAIM_COMPLETED: '클레임완료',
  CLAIM_FAILED: '클레임실패',
  CONFIRMED: '구매확정',
  CANCELED: '취소',
  UNKNOWN: '알 수 없음',
};

// ---------------------------------------------------------------------------
// 마일리지 (mileage)
// ---------------------------------------------------------------------------

export const MILEAGE_TX_TYPE_LABELS: LabelMap = {
  EARN: '적립',
  USE: '사용',
  EXPIRE: '만료',
  ADJUST: '조정',
};

export const DEPOSIT_WITHDRAWAL_STATUS_LABELS: LabelMap = {
  REQUESTED: '신청',
  APPROVED: '승인',
  PAID: '지급완료',
  REJECTED: '반려',
};
